import { useEffect } from 'react'
import { usePostHog } from '@posthog/react'
import { useV2Customer, useV2Validate } from './api/queries'
import { errorMessage } from './api/client'
import { config, isConfigured } from './config/env'
import { messageForCode } from './screens/v3/error-messages'
import { getLightspeedApi, isPosHost } from './lightspeed/pos-api'
import { useFlow } from './flow/FlowContext'
import { useSession } from './session/SessionContext'
import {
  posContextGroupKey,
  posContextProperties,
  usePosContext,
} from './lightspeed/usePosContext'
import { BootLoadingScreen } from './screens/BootLoadingScreen'
import { ErrorScreen } from './screens/ErrorScreen'
import { IdleScreen } from './screens/IdleScreen'
import { ScanFlow } from './screens/ScanFlow'
import { MarketplaceScreen } from './screens/MarketplaceScreen'
import { CustomerScreen } from './screens/v3/CustomerScreen'
import { toLinkedCustomer, toRewardOptions } from './api/v2/adapt'
import { RedeemedScreen } from './screens/RedeemedScreen'
import { DebugPanel } from './components/DebugPanel'
import { VersionBadge } from './components/VersionBadge'
import { isDebugEnabled } from './config/debug'
import { captureAccountDump } from './debug/accountDump'

/** Name the missing half rather than listing both. A cashier reading this is going to
 * relay it to whoever set the till up, and "one of these two things" is a worse message
 * to relay than the one that is actually wrong.
 *
 * Module scope: it closes over nothing and was being rebuilt on every render.
 */
function configurationProblem(): string {
  if (!config.posSurfaceUrl) {
    return 'Not configured: VITE_POS_SURFACE_URL is unset (see .env.example).'
  }
  return 'This till has no Badger credential. Re-link the location in the dashboard and update the extension URL.'
}

export default function App() {
  const { state, dispatch } = useFlow()
  const { reset, scannedQr } = useSession()
  const validate = useV2Validate()
  const posthog = usePostHog()
  // DEV-378. The scanned customer, from the V2 surface. `scannedQr` is whatever the till
  // read; the surface resolves it to a uid itself, so nothing here has to know which of
  // the accepted forms it got.
  const customer = useV2Customer(scannedQr, validate.data?.location_id)
  const posContext = usePosContext()

  // Tag all telemetry with the Lightspeed business/location/device/cashier so this
  // PoC session is debuggable in PostHog (super properties + a location group whose key
  // matches the backend business_location_id). No-op outside the POS host.
  useEffect(() => {
    if (!posContext) return
    posthog?.register(posContextProperties(posContext))
    const groupKey = posContextGroupKey(posContext)
    if (groupKey) {
      posthog?.group('lightspeed_location', groupKey, {
        name: posContext.businessName,
        location_name: posContext.locationName,
        business_id: posContext.businessId,
        location_id: posContext.locationId,
      })
    }
  }, [posContext, posthog])

  // Dump the ticket on every EXTENSION OPEN, not just when a customer is linked.
  //
  // The dumps used to fire from the linked-customer panel, so opening the extension to look at a
  // basket produced nothing at all — exactly the case you most want to inspect, since the whole
  // point is to see what the POS says about a ticket before anyone is scanned. Debug-gated inside
  // `captureAccountDump`.
  useEffect(() => {
    void captureAccountDump(posthog, 'app_open')
  }, [posthog])

  useEffect(() => {
    if (validate.isError) {
      // DEV-378: the V2 surface refuses with a typed code, and the client owns the
      // words (§16.5). `messageForCode` is the one place that mapping lives, so a
      // boot failure reads the same as the same refusal anywhere else in the app.
      const code = (validate.error as { code?: string } | null)?.code
      dispatch({
        type: 'VALIDATE_FAILED',
        message: code ? messageForCode(code).body : errorMessage(validate.error),
        // The code travels WITH the message. The fallback copy asks the cashier to report
        // it, so dropping it here left them reading "report the code below" above nothing.
        code,
      })
      return
    }
    if (!validate.isSuccess) return
    // Brief hold so the loading bar is visible (validation is usually fast), then advance.
    const timer = setTimeout(() => dispatch({ type: 'VALIDATED' }), 700)
    return () => clearTimeout(timer)
  }, [validate.isSuccess, validate.isError, validate.error, dispatch])

  // A scan is only finished when the customer is on screen. The old flow waited on a V1
  // query that is now correctly disabled, which is why scanning appeared to do nothing.
  useEffect(() => {
    if (state.step !== 'pointsGiven') return
    if (customer.isError) {
      const code = (customer.error as { code?: string } | null)?.code
      dispatch({
        type: 'CUSTOMER_FAILED',
        message: code ? messageForCode(code).body : errorMessage(customer.error),
        code,
      })
      return
    }
    if (customer.isSuccess) dispatch({ type: 'SHOW_INFO' })
  }, [state.step, customer.isSuccess, customer.isError, customer.error, dispatch])

  function nextCustomer() {
    reset()
    dispatch({ type: 'RESET' })
  }

  // The discreet ✕ on every step: abandon the session — close the extension inside the POS
  // (cashier returns to Lightspeed), or reset to the scan screen in a plain browser.
  function abandon() {
    if (isPosHost()) {
      getLightspeedApi().close()
      return
    }
    nextCustomer()
  }

  // The active screen for the current flow step. The debug panel (when enabled) is overlaid
  // on top of whichever screen is showing.
  function currentScreen() {
    if (!isConfigured()) {
      return (
        <ErrorScreen
          message={configurationProblem()}
          onClose={abandon}
        />
      )
    }
    switch (state.step) {
      case 'validating':
        return <BootLoadingScreen onClose={abandon} />
      case 'idle':
        return <IdleScreen />
      case 'pointsGiven':
        return <ScanFlow />
      case 'customerInfo': {
        // ponytail: `MarketplaceScreen` and `CustomerInfoScreen` stay in the tree
        // unrouted. They are the V1/stamp-model reference the points claim path is
        // ported from, and the design that replaces them is still landing.
        if (!customer.data) return <BootLoadingScreen onClose={abandon} />
        // `permits_value_actions` is the server's answer, and it is the only thing that
        // decides whether a reward can be offered — see `rewardStateOf`. The client
        // never derives availability itself.
        const locationAvailable = validate.data?.permits_value_actions ?? false
        return (
          <CustomerScreen
            customer={toLinkedCustomer(customer.data, { name: scannedQr ?? '' })}
            rewards={toRewardOptions(customer.data, { locationAvailable })}
            tab="profile"
            onTabChange={() => {}}
            onUnlink={abandon}
            onDone={abandon}
            onUseReward={() => {}}
          />
        )
      }
      case 'redeemed':
        return state.redemption ? (
          <RedeemedScreen
            redemption={state.redemption}
            onContinue={() => dispatch({ type: 'SHOW_INFO' })}
            onClose={abandon}
          />
        ) : (
          <MarketplaceScreen onClose={abandon} onDone={abandon} />
        )
      case 'error':
        return (
          <ErrorScreen
            message={state.error ?? 'Unexpected error'}
            code={state.errorCode}
            onRetry={nextCustomer}
            onClose={abandon}
          />
        )
    }
  }

  return (
    <>
      {currentScreen()}
      <VersionBadge />
      {isDebugEnabled() && <DebugPanel />}
    </>
  )
}
